const COMPONENTES_CON_ACTUALIZACION_POR_DEFECTO = new Set([
  "select",
  "enum",
]);

export const VALOR_TODAS_SELECT = "__TODAS__";

export const OPCION_TODAS = {
  value: VALOR_TODAS_SELECT,
  label: "Todas",
  sin_filtro: true,
};

export const esValorTodasSelect = (valor) =>
  valor === VALOR_TODAS_SELECT ||
  (typeof valor === "string" && valor.trim() === "");

export const opcionEsTodas = (opcion) =>
  opcion?.sin_filtro === true || esValorTodasSelect(opcion?.value);

const normalizarOpcionSelect = (opcion) =>
  opcionEsTodas(opcion)
    ? { ...opcion, value: VALOR_TODAS_SELECT, sin_filtro: true }
    : opcion;

export const normalizarValorFiltroParaSelect = (
  valor,
  incluirTodas = true
) => {
  const sinValor =
    valor === null || valor === undefined || esValorTodasSelect(valor);

  if (!sinValor) return valor;
  return incluirTodas ? VALOR_TODAS_SELECT : "";
};

export const aplicarValorSelectAFiltros = ({
  filtros,
  id_a,
  clave_envio,
  valor,
}) => {
  const filtrosActualizados = { ...filtros };

  if (
    valor === null ||
    valor === undefined ||
    esValorTodasSelect(valor)
  ) {
    const tieneFiltro = Object.prototype.hasOwnProperty.call(filtros, id_a);
    const tieneClaveEnvio =
      clave_envio &&
      Object.prototype.hasOwnProperty.call(filtros, clave_envio);

    if (!tieneFiltro && !tieneClaveEnvio) return filtros;

    delete filtrosActualizados[id_a];
    if (clave_envio) delete filtrosActualizados[clave_envio];
    return filtrosActualizados;
  }

  filtrosActualizados[id_a] = valor;
  if (clave_envio) filtrosActualizados[clave_envio] = valor;

  return filtrosActualizados;
};

export const obtenerValorFiltroConfigurado = ({
  filtros = {},
  id_a,
  clave_envio,
}) => {
  if (Object.prototype.hasOwnProperty.call(filtros, id_a)) {
    return { encontrado: true, valor: filtros[id_a] };
  }

  const claveEnvio = clave_envio?.trim();
  if (
    claveEnvio &&
    Object.prototype.hasOwnProperty.call(filtros, claveEnvio)
  ) {
    return { encontrado: true, valor: filtros[claveEnvio] };
  }

  return { encontrado: false, valor: undefined };
};

export const restaurarValoresTodasEnFiltros = ({
  filtros = {},
  filtrosOriginales = {},
  configuracion = [],
}) => {
  const filtrosRestaurados = { ...filtros };

  const restaurarFiltro = (filtro) => {
    const filtroOriginal = obtenerValorFiltroConfigurado({
      filtros: filtrosOriginales,
      id_a: filtro.id_a,
      clave_envio: filtro.clave_envio,
    });

    if (
      filtroOriginal.encontrado &&
      esValorTodasSelect(filtroOriginal.valor)
    ) {
      filtrosRestaurados[filtro.id_a] = VALOR_TODAS_SELECT;
    }

    if (Array.isArray(filtro.sc_hijos)) {
      filtro.sc_hijos.forEach(restaurarFiltro);
    }
  };

  configuracion.forEach(restaurarFiltro);
  return filtrosRestaurados;
};

const valorEstaHabilitado = (valor) => {
  if (valor === true || valor === 1) return true;
  if (typeof valor !== "string") return false;

  return ["s", "si", "true", "1"].includes(valor.trim().toLowerCase());
};

export const valoresDeOpcionIguales = (valorA, valorB) => {
  if (valorA === valorB) return true;
  if (esValorTodasSelect(valorA) && esValorTodasSelect(valorB)) return true;
  if (valorA === null || valorA === undefined) return false;
  if (valorB === null || valorB === undefined) return false;

  return String(valorA) === String(valorB);
};

export const debeActualizarOpcionesFiltro = ({
  componente,
  filtro_actualizar_opciones,
}) => {
  const valorConfigurado = filtro_actualizar_opciones
    ?.toString()
    .trim()
    .toLowerCase();

  if (valorConfigurado === "s") return true;
  if (valorConfigurado === "n") return false;

  return COMPONENTES_CON_ACTUALIZACION_POR_DEFECTO.has(componente);
};

export const componerOpcionesSelect = (opciones, incluirTodas = true) => {
  const opcionesCompuestas = Array.isArray(opciones)
    ? opciones.map(normalizarOpcionSelect)
    : [];

  if (!incluirTodas) return opcionesCompuestas;

  const yaIncluyeTodas = opcionesCompuestas.some(opcionEsTodas);

  return yaIncluyeTodas
    ? opcionesCompuestas
    : [{ ...OPCION_TODAS }, ...opcionesCompuestas];
};

export const buscarOpcionPorValor = (opciones, valor) =>
  opciones.find((opcion) => valoresDeOpcionIguales(opcion?.value, valor));

export const buscarOpcionDefault = (opciones) =>
  opciones.find((opcion) => valorEstaHabilitado(opcion?.default));

export const opcionEstaDeshabilitada = (opcion) =>
  valorEstaHabilitado(opcion?.disabled);

export const aplicarOpcionDefaultAFiltros = ({
  filtros,
  id_a,
  clave_envio,
  opcionDefault,
}) => {
  if (opcionEsTodas(opcionDefault)) {
    return aplicarValorSelectAFiltros({
      filtros,
      id_a,
      clave_envio,
      valor: VALOR_TODAS_SELECT,
    });
  }

  const valorDefault = opcionEstaDeshabilitada(opcionDefault)
    ? undefined
    : opcionDefault.value;
  const mismoValor = valoresDeOpcionIguales(filtros[id_a], valorDefault);
  const mismoValorClaveEnvio =
    !clave_envio ||
    valoresDeOpcionIguales(filtros[clave_envio], valorDefault);

  if (mismoValor && mismoValorClaveEnvio) return filtros;

  const filtrosActualizados = {
    ...filtros,
    [id_a]: valorDefault,
  };

  if (clave_envio) {
    filtrosActualizados[clave_envio] = valorDefault;
  }

  return filtrosActualizados;
};

export const reconciliarOpcionesSelect = ({
  opcionesNuevas,
  opcionesAnteriores,
  valorSeleccionado,
  incluirTodas = true,
}) => {
  const opcionesCompuestas = componerOpcionesSelect(
    opcionesNuevas,
    incluirTodas
  );

  const valorVacio =
    valorSeleccionado === null ||
    valorSeleccionado === undefined ||
    esValorTodasSelect(valorSeleccionado);

  if (valorVacio || buscarOpcionPorValor(opcionesCompuestas, valorSeleccionado)) {
    return opcionesCompuestas;
  }

  const opcionSeleccionadaAnterior = buscarOpcionPorValor(
    opcionesAnteriores ?? [],
    valorSeleccionado
  );

  if (!opcionSeleccionadaAnterior) return opcionesCompuestas;

  return [
    {
      ...opcionSeleccionadaAnterior,
      disabled: true,
      opcion_desactualizada: true,
    },
    ...opcionesCompuestas,
  ];
};
